import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Bell,
  Bookmark,
  BriefcaseBusiness,
  Building2,
  Camera,
  Check,
  ChevronRight,
  Globe2,
  LayoutDashboard,
  Loader2,
  LockKeyhole,
  LogOut,
  MailCheck,
  MapPin,
  Search,
  Settings,
  Shield,
  ShieldAlert,
  Trash2,
  Upload,
  User,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { GoldButton, Pill } from "@/components/Cards";
import { Input } from "@/pages/Auth";
import { CityPicker, CountryPicker } from "@/components/SignupLocationSelect";
import { FEATURED_CITIES } from "@/data/locations";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "profile", label: "My Profile", icon: User },
  { key: "saved", label: "Saved Content", icon: Bookmark },
  { key: "network", label: "My Network", icon: Users },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "settings", label: "Account Settings", icon: Settings },
];
const panel = "rounded-2xl border border-dir-gold/15 bg-dir-card";
const control =
  "min-h-12 w-full rounded-xl border border-dir-gold/20 bg-dir-elevated px-4 py-3 text-sm text-dir-text transition placeholder:text-dir-faint hover:border-dir-gold/40 focus:border-dir-gold focus:ring-2 focus:ring-dir-gold/15";

export default function Dashboard() {
  const { user, setUser, logout, isStaff } = useAuth(),
    [tab, setTab] = useState("overview"),
    [saved, setSaved] = useState([]),
    [news, setNews] = useState([]),
    [summary, setSummary] = useState({}),
    [members, setMembers] = useState([]),
    [notifications, setNotifications] = useState([]),
    [connections, setConnections] = useState([]),
    [loading, setLoading] = useState(true);
  const refresh = () =>
    Promise.all([
      api.get("/me/saved"),
      api.get("/news?limit=3"),
      api.get("/me/platform-summary"),
      api.get("/members", { params: { limit: 6 } }),
      api.get("/me/notifications"),
      api.get("/me/connections"),
    ])
      .then(([a, b, c, d, e, f]) => {
        setSaved(a.data);
        setNews(b.data);
        setSummary(c.data || {});
        setMembers(d.data);
        setNotifications(e.data);
        setConnections(f.data);
      })
      .catch(() =>
        toast.error("Could not load all member data. Please try again."),
      )
      .finally(() => setLoading(false));
  useEffect(() => {
    refresh();
  }, []);
  if (!user) return null;
  const resend = () =>
    api
      .post("/auth/resend-verification")
      .then(() => toast.success("Verification email sent."))
      .catch(() => toast.error("Could not send verification email."));
  return (
    <div
      className="min-h-screen overflow-x-hidden bg-dir-bg pt-[72px]"
      data-testid="dashboard-page"
    >
      <div className="border-b border-dir-gold/15 bg-dir-deep/70">
        <div className="mx-auto flex max-w-[1400px] items-center gap-4 px-4 py-5 sm:px-6 lg:px-10">
          <Avatar user={user} size="lg" />
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[.18em] text-dir-gold">
              Member Dashboard
            </p>
            <h1 className="truncate font-display text-xl font-bold text-dir-text sm:text-2xl">
              {fullName(user)}
            </h1>
            <p className="truncate text-xs text-dir-muted">
              {[user.profession, user.organization, user.country]
                .filter(Boolean)
                .join(" · ") || "Complete your profile to introduce yourself"}
            </p>
          </div>
        </div>
      </div>
      <div className="mx-auto flex max-w-[1400px] gap-8 px-4 py-6 sm:px-6 lg:px-10 lg:py-10">
        <aside className="hidden w-60 shrink-0 lg:block">
          <div className="sticky top-24 space-y-1">
            {NAV.map((n) => (
              <NavButton
                key={n.key}
                n={n}
                active={tab === n.key}
                onClick={() => setTab(n.key)}
              />
            ))}
            {isStaff && (
              <Link
                to="/admin"
                className="flex min-h-11 items-center gap-3 rounded-xl px-4 py-3 text-sm text-dir-muted hover:bg-dir-card hover:text-dir-text"
              >
                <Shield className="h-4 w-4" />
                Admin Studio
              </Link>
            )}
            <button
              onClick={logout}
              className="flex min-h-11 w-full items-center gap-3 rounded-xl px-4 py-3 text-sm text-dir-muted hover:bg-dir-card hover:text-dir-text"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </aside>
        <main className="min-w-0 flex-1">
          {!user.email_verified && (
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dir-gold/30 bg-dir-gold/10 p-4">
              <div className="flex items-center gap-3 text-sm text-dir-text">
                <ShieldAlert className="h-5 w-5 shrink-0 text-dir-gold" />
                Verify your email to unlock full membership.
              </div>
              <button
                onClick={resend}
                className="min-h-11 rounded-full border border-dir-gold/40 px-4 text-sm text-dir-gold"
              >
                Resend Email
              </button>
            </div>
          )}
          <div
            className="-mx-1 mb-6 flex gap-2 overflow-x-auto px-1 pb-2 lg:hidden"
            aria-label="Member sections"
          >
            {NAV.map((n) => (
              <button
                key={n.key}
                onClick={() => setTab(n.key)}
                className={`min-h-11 shrink-0 rounded-full px-4 text-xs font-semibold ${tab === n.key ? "bg-dir-gold text-dir-deep" : "border border-dir-gold/25 text-dir-muted"}`}
              >
                {n.label}
              </button>
            ))}
          </div>
          {loading ? (
            <DashboardSkeleton />
          ) : (
            <>
              {tab === "overview" && (
                <Overview
                  user={user}
                  saved={saved}
                  news={news}
                  summary={summary}
                  members={members}
                  setTab={setTab}
                />
              )}{" "}
              {tab === "profile" && (
                <ProfileTab user={user} setUser={setUser} />
              )}{" "}
              {tab === "saved" && (
                <SavedTab saved={saved} onChanged={refresh} />
              )}{" "}
              {tab === "network" && (
                <NetworkTab
                  user={user}
                  members={members}
                  connections={connections}
                  onChanged={refresh}
                />
              )}{" "}
              {tab === "notifications" && (
                <NotificationsTab rows={notifications} onChanged={refresh} />
              )}{" "}
              {tab === "settings" && (
                <AccountSettings
                  user={user}
                  setUser={setUser}
                  logout={logout}
                />
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

function NavButton({ n, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex min-h-11 w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium ${active ? "border border-dir-gold/30 bg-dir-gold/10 text-dir-gold" : "text-dir-muted hover:bg-dir-card hover:text-dir-text"}`}
    >
      <n.icon className="h-4 w-4" />
      {n.label}
    </button>
  );
}
const fullName = (u) =>
  u.display_name ||
  `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
  "DIR Nation Member";
function Avatar({ user, size = "md" }) {
  const initials =
      `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase() ||
      "DN",
    sizes = size === "lg" ? "h-16 w-16 text-lg" : "h-12 w-12 text-sm";
  return user.avatar_url ? (
    <img
      src={user.avatar_url}
      alt={`${fullName(user)} profile`}
      className={`${sizes} shrink-0 rounded-full border-2 border-dir-gold/35 object-cover`}
    />
  ) : (
    <div
      aria-label={`${fullName(user)} initials`}
      className={`${sizes} flex shrink-0 items-center justify-center rounded-full border border-dir-gold/35 bg-dir-gold/10 font-bold text-dir-gold`}
    >
      {initials}
    </div>
  );
}

function completion(user) {
  const fields = [
    "avatar_url",
    "first_name",
    "last_name",
    "country",
    "city",
    "profession",
    "organisation",
    "bio",
    "area_of_interest",
  ];
  return Math.round(
    (fields.filter((k) =>
      Boolean(user[k] || user[k === "organisation" ? "organization" : k]),
    ).length /
      fields.length) *
      100,
  );
}
function Overview({ user, saved, news, summary, members, setTab }) {
  const percent = completion(user),
    stats = [
      { label: "Profile Completion", value: `${percent}%`, icon: User },
      {
        label: "Saved Content",
        value: summary.saved ?? saved.length,
        icon: Bookmark,
      },
      { label: "Connections", value: summary.connections ?? 0, icon: Users },
      {
        label: "Role",
        value: (user.role || "member").replaceAll("_", " "),
        icon: Shield,
      },
      {
        label: "Verification",
        value: user.email_verified ? "Verified" : "Pending",
        icon: MailCheck,
      },
    ];
  return (
    <div className="space-y-7">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-dir-text">
            Welcome back, {user.first_name || "Member"}
          </h2>
          <p className="mt-1 text-sm text-dir-muted">
            Your community activity and next steps.
          </p>
        </div>
        {percent < 100 && (
          <GoldButton onClick={() => setTab("profile")}>
            Complete Your Profile <ChevronRight className="h-4 w-4" />
          </GoldButton>
        )}
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {stats.map((s) => (
          <div key={s.label} className={`${panel} p-5`}>
            <s.icon className="h-5 w-5 text-dir-gold" />
            <div className="mt-3 truncate font-display text-xl font-bold capitalize text-dir-text">
              {s.value}
            </div>
            <div className="mt-1 text-xs text-dir-faint">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <Preview
          title="Suggested Connections"
          action="Explore community"
          onAction={() => setTab("network")}
        >
          {members.slice(0, 3).map((m) => (
            <div key={m.id} className="flex items-center gap-3 py-3">
              <Avatar user={m} />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-dir-text">
                  {fullName(m)}
                </p>
                <p className="truncate text-xs text-dir-muted">
                  {[m.profession, m.country].filter(Boolean).join(" · ")}
                </p>
              </div>
            </div>
          ))}
          {!members.length && (
            <CompactEmpty
              icon={Users}
              title="No suggestions available yet"
              text="Check again as the community grows."
            />
          )}
        </Preview>
        <Preview
          title="Recent Saved Content"
          action="View saved"
          onAction={() => setTab("saved")}
        >
          {saved.slice(0, 3).map((s) => (
            <Link
              key={s.id}
              to={s.url}
              className="flex min-h-11 items-center justify-between border-b border-dir-gold/10 py-3 text-sm text-dir-text last:border-0"
            >
              <span className="truncate">{s.title}</span>
              <Pill>{s.item_type}</Pill>
            </Link>
          ))}
          {!saved.length && (
            <CompactEmpty
              icon={Bookmark}
              title="Nothing saved yet"
              text="Explore articles and save what matters."
            />
          )}
        </Preview>
      </div>
      {news.length > 0 && (
        <section>
          <h2 className="mb-4 font-display text-lg font-semibold text-dir-text">
            Latest News
          </h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {news.map((n) => (
              <Link
                key={n.id}
                to={`/news/${n.slug}`}
                className={`${panel} overflow-hidden transition hover:border-dir-gold/45`}
              >
                {n.image && (
                  <img
                    src={n.image}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.hidden = true;
                    }}
                    className="aspect-[16/9] w-full object-cover"
                  />
                )}
                <div className="p-4">
                  <h3 className="line-clamp-2 text-sm font-semibold text-dir-text">
                    {n.headline || n.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
function Preview({ title, action, onAction, children }) {
  return (
    <section className={`${panel} p-5`}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-lg text-dir-text">{title}</h2>
        <button
          onClick={onAction}
          className="min-h-11 text-xs font-semibold text-dir-gold"
        >
          {action}
        </button>
      </div>
      <div className="mt-2">{children}</div>
    </section>
  );
}

function ProfileTab({ user, setUser }) {
  const [f, setF] = useState({ ...user }),
    [saving, setSaving] = useState(false),
    [heritage, setHeritage] = useState([]);
  useEffect(() => {
    api
      .get("/heritage")
      .then((r) => setHeritage(r.data.communities || []))
      .catch(() => {});
  }, []);
  const selected = heritage.find((x) => x.id === f.heritage_community_id),
    branches = (selected?.major_branches || []).map((x) => x.name);
  const patch = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/me/profile", f);
      setUser({ ...user, ...data, organization: data.organisation });
      toast.success("Profile updated successfully.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} className="mx-auto max-w-4xl space-y-7">
      <SectionTitle
        title="My Profile"
        text="Control how you appear across the DIR Nation community."
      />
      <AvatarUpload
        user={f}
        onChange={(url) => patch("avatar_url", url)}
        setUser={setUser}
      />
      <FormSection title="Personal Information">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="First Name"
            value={f.first_name || ""}
            onChange={(e) => patch("first_name", e.target.value)}
            required
          />
          <Input
            label="Last Name"
            value={f.last_name || ""}
            onChange={(e) => patch("last_name", e.target.value)}
            required
          />
          <CountryPicker
            value={f.country || ""}
            onChange={(item) => {
              patch("country", item.name);
              patch("country_code", item.code);
              patch("city", "");
            }}
          />
          <CityPicker
            country={f.country}
            items={[
              ...(FEATURED_CITIES[f.country] || []).map((name) => ({ name })),
              { name: "Other / Enter city manually" },
            ]}
            value={f.city || ""}
            onChange={(v) => patch("city", v)}
          />
        </div>
      </FormSection>
      <FormSection title="Professional Information">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Profession"
            value={f.profession || ""}
            onChange={(e) => patch("profession", e.target.value)}
          />
          <Input
            label="Organization"
            value={f.organization || f.organisation || ""}
            onChange={(e) => patch("organization", e.target.value)}
          />
          <Input
            label="Job Title"
            value={f.job_title || ""}
            onChange={(e) => patch("job_title", e.target.value)}
          />
          <Input
            label="LinkedIn"
            type="url"
            value={f.linkedin || ""}
            onChange={(e) => patch("linkedin", e.target.value)}
          />
          <Input
            label="Website"
            type="url"
            value={f.website || ""}
            onChange={(e) => patch("website", e.target.value)}
          />
          <Input
            label="Area of Interest"
            value={f.area_of_interest || ""}
            onChange={(e) => patch("area_of_interest", e.target.value)}
          />
        </div>
        <Label text="Biography">
          <textarea
            rows="5"
            value={f.bio || ""}
            onChange={(e) => patch("bio", e.target.value)}
            className={control}
            placeholder="Share a short professional and community introduction."
          />
        </Label>
      </FormSection>
      <FormSection
        title="Community Information"
        text="Heritage information is optional, self-declared and never affects access or profile completion."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Label text="DIR Heritage / Community">
            <select
              value={f.heritage_community_id || ""}
              onChange={(e) => {
                patch("heritage_community_id", e.target.value || null);
                patch("heritage_branch", "");
              }}
              className={control}
            >
              <option value="">Prefer not to say</option>
              {heritage.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </select>
          </Label>
          <Label text="Major Branch (optional)">
            <select
              disabled={!selected}
              value={f.heritage_branch || ""}
              onChange={(e) => patch("heritage_branch", e.target.value)}
              className={control}
            >
              <option value="">Not specified</option>
              {branches.map((name) => (
                <option key={name}>{name}</option>
              ))}
            </select>
          </Label>
          <Input
            label="Sub-branch (optional)"
            value={f.heritage_subbranch || ""}
            onChange={(e) => patch("heritage_subbranch", e.target.value)}
          />
          <Label text="Heritage Visibility">
            <select
              value={f.heritage_visibility || "members"}
              onChange={(e) => patch("heritage_visibility", e.target.value)}
              className={control}
            >
              <option value="members">Members Only</option>
              <option value="private">Private</option>
              <option value="public">Public</option>
            </select>
          </Label>
        </div>
      </FormSection>
      <FormSection title="Profile Privacy">
        <div className="grid gap-4 sm:grid-cols-2">
          <Label text="Profile Visibility">
            <select
              value={f.profile_visibility || f.privacy || "members"}
              onChange={(e) => patch("profile_visibility", e.target.value)}
              className={control}
            >
              <option value="public">Public</option>
              <option value="members">Members Only</option>
              <option value="private">Private</option>
            </select>
          </Label>
          <Label text="Contact Visibility">
            <select
              value={f.contact_visibility || "private"}
              onChange={(e) => patch("contact_visibility", e.target.value)}
              className={control}
            >
              <option value="private">Private</option>
              <option value="members">Members Only</option>
              <option value="public">Public</option>
            </select>
          </Label>
        </div>
      </FormSection>
      <GoldButton disabled={saving}>
        {saving ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Saving…
          </>
        ) : (
          <>
            <Check className="h-4 w-4" />
            Save Changes
          </>
        )}
      </GoldButton>
    </form>
  );
}

function AvatarUpload({ user, onChange, setUser }) {
  const input = useRef(null),
    [uploading, setUploading] = useState(false),
    [drag, setDrag] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/me/avatar", form);
      onChange(data.avatar_url);
      setUser((u) => ({ ...u, avatar_url: data.avatar_url }));
      toast.success("Photo uploaded successfully.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        upload(e.dataTransfer.files[0]);
      }}
      className={`flex flex-col items-center gap-4 rounded-2xl border border-dashed p-6 text-center sm:flex-row sm:text-left ${drag ? "border-dir-gold bg-dir-gold/10" : "border-dir-gold/25 bg-dir-deep"}`}
    >
      <Avatar user={user} size="lg" />
      <div className="flex-1">
        <h3 className="font-semibold text-dir-text">Profile Photo</h3>
        <p className="mt-1 text-xs leading-5 text-dir-muted">
          JPEG, PNG or WebP up to 5 MB. Drag and drop or choose a photo.
        </p>
      </div>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(e) => upload(e.target.files[0])}
      />
      <button
        type="button"
        disabled={uploading}
        onClick={() => input.current?.click()}
        className="inline-flex min-h-11 items-center gap-2 rounded-full border border-dir-gold/35 px-4 text-sm font-semibold text-dir-gold"
      >
        {uploading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Camera className="h-4 w-4" />
        )}
        {uploading ? "Uploading…" : "Choose Photo"}
      </button>
    </div>
  );
}

function SavedTab({ saved, onChanged }) {
  const remove = async (s) => {
    try {
      await api.post("/me/saved", {
        item_id: s.item_id,
        item_type: s.item_type,
        title: s.title,
        url: s.url,
      });
      toast.success("Removed from saved content.");
      onChanged();
    } catch {
      toast.error("Could not remove saved content.");
    }
  };
  return (
    <div>
      <SectionTitle
        title="Saved Content"
        text="Articles, news and opportunities you want to revisit."
      />
      {!saved.length ? (
        <CompactEmpty
          icon={Bookmark}
          title="No saved content yet"
          text="Explore articles and save what matters to you."
          action="Explore Articles"
          to="/articles"
        />
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {saved.map((s) => (
            <article
              key={s.id}
              className={`${panel} flex min-w-0 items-center gap-4 p-5`}
            >
              <Bookmark className="h-5 w-5 shrink-0 text-dir-gold" />
              <Link to={s.url} className="min-w-0 flex-1">
                <Pill>{s.item_type}</Pill>
                <h3 className="mt-2 truncate text-sm font-semibold text-dir-text">
                  {s.title}
                </h3>
              </Link>
              <button
                onClick={() => remove(s)}
                aria-label={`Remove ${s.title}`}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-dir-muted hover:bg-red-500/10 hover:text-red-400"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function NetworkTab({ user, members, connections, onChanged }) {
  const [q, setQ] = useState(""),
    [rows, setRows] = useState(members),
    [relations, setRelations] = useState(connections),
    [loading, setLoading] = useState(false),
    [processing, setProcessing] = useState(new Set());
  useEffect(() => setRelations(connections), [connections]);
  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api
        .get("/members", { params: { q, limit: 24 } })
        .then((r) => setRows(r.data))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const setBusy = (id, value) =>
    setProcessing((current) => {
      const next = new Set(current);
      value ? next.add(id) : next.delete(id);
      return next;
    });
  const connect = async (member) => {
    if (processing.has(member.id) || member.id === user.id) return;
    setBusy(member.id, true);
    try {
      const { data } = await api.post("/me/connections", {
        recipient_id: member.id,
      });
      if (data.state === "pending_received")
        toast.info("This member has already sent you a request.");
      else if (data.state === "connected")
        toast.info("You are already connected.");
      else toast.success(data.message || "Connection request sent.");
      await onChanged();
    } catch {
      toast.error("Could not send connection request. Please try again.");
    } finally {
      setBusy(member.id, false);
    }
  };
  const act = async (connection, action) => {
    if (processing.has(connection.connection_id)) return;
    const previous = relations;
    setBusy(connection.connection_id, true);
    setRelations((current) =>
      action === "remove"
        ? current.filter((x) => x.connection_id !== connection.connection_id)
        : current.map((x) =>
            x.connection_id === connection.connection_id
              ? {
                  ...x,
                  status:
                    action === "accept"
                      ? "accepted"
                      : action === "decline"
                        ? "declined"
                        : "cancelled",
                }
              : x,
          ),
    );
    try {
      await api.put(`/me/connections/${connection.connection_id}`, { action });
      toast.success(
        {
          accept: "Connection accepted.",
          decline: "Request declined.",
          cancel: "Request cancelled.",
          remove: "Connection removed.",
        }[action],
      );
      await onChanged();
    } catch {
      setRelations(previous);
      toast.error("Could not update this connection. Please try again.");
    } finally {
      setBusy(connection.connection_id, false);
    }
  };
  const activeMemberIds = new Set(
    relations
      .filter((x) => ["pending", "accepted", "blocked"].includes(x.status))
      .map((x) => x.member_id),
  );
  const suggested = rows.filter(
    (m) => m.id !== user.id && !activeMemberIds.has(m.id),
  );
  const received = relations.filter(
    (x) => x.status === "pending" && x.direction === "received",
  );
  const sent = relations.filter(
    (x) => x.status === "pending" && x.direction === "sent",
  );
  const accepted = relations.filter((x) => x.status === "accepted");
  return (
    <div>
      <SectionTitle
        title="My Network"
        text="Build meaningful professional and community connections."
      />
      <div className="relative mt-6">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-dir-gold" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search members"
          placeholder="Search by name, country, city, profession or organization"
          className={`${control} pl-11`}
        />
      </div>
      {relations.length === 0 && (
        <div className="mt-6 rounded-2xl border border-dir-gold/20 bg-dir-gold/5 p-5">
          <h3 className="font-display text-lg text-dir-text">
            Grow Your Network
          </h3>
          <p className="mt-2 text-sm text-dir-muted">
            Discover members below or explore DIR Nation's global community.
          </p>
          <Link
            to="/global-network"
            className="mt-3 inline-flex min-h-11 items-center text-sm font-semibold text-dir-gold"
          >
            Explore Global Network <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      )}
      <NetworkSection
        title="Connection Requests"
        description="Requests you have received"
        rows={received}
        empty="No received requests."
        actions={(connection) => (
          <>
            <ConnectionAction
              primary
              disabled={processing.has(connection.connection_id)}
              onClick={() => act(connection, "accept")}
            >
              {processing.has(connection.connection_id)
                ? "Updating…"
                : "Accept"}
            </ConnectionAction>
            <ConnectionAction
              disabled={processing.has(connection.connection_id)}
              onClick={() => act(connection, "decline")}
            >
              Decline
            </ConnectionAction>
          </>
        )}
      />
      <NetworkSection
        title="Sent Requests"
        description="Requests awaiting a response"
        rows={sent}
        empty="No sent requests."
        actions={(connection) => (
          <ConnectionAction
            disabled={processing.has(connection.connection_id)}
            onClick={() => act(connection, "cancel")}
          >
            {processing.has(connection.connection_id)
              ? "Cancelling…"
              : "Cancel Request"}
          </ConnectionAction>
        )}
      />
      <NetworkSection
        title="My Connections"
        description="Members in your DIR Nation network"
        rows={accepted}
        empty="No accepted connections yet."
        connected
        actions={(connection) => (
          <ConnectionAction
            disabled={processing.has(connection.connection_id)}
            onClick={() => act(connection, "remove")}
          >
            Remove
          </ConnectionAction>
        )}
      />
      <h3 className="mt-8 font-display text-lg text-dir-text">
        Suggested Members
      </h3>
      {loading ? (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <SkeletonCards count={6} />
        </div>
      ) : suggested.length ? (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {suggested.map((m) => (
            <article key={m.id} className={`${panel} flex flex-col p-5`}>
              <div className="flex items-center gap-3">
                <Avatar user={m} />
                <div className="min-w-0">
                  <h4 className="truncate font-semibold text-dir-text">
                    {fullName(m)}
                  </h4>
                  <p className="truncate text-xs text-dir-muted">
                    {[m.country, m.city].filter(Boolean).join(" · ")}
                  </p>
                </div>
              </div>
              {[m.job_title || m.profession, m.organization].filter(Boolean)
                .length > 0 && (
                <p className="mt-4 line-clamp-2 min-h-10 text-xs leading-5 text-dir-muted">
                  {[m.job_title || m.profession, m.organization]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              )}
              <button
                disabled={processing.has(m.id)}
                aria-busy={processing.has(m.id)}
                onClick={() => connect(m)}
                className="mt-4 inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-dir-gold/35 px-4 text-sm font-semibold text-dir-gold disabled:opacity-50"
              >
                {processing.has(m.id) ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4" />
                )}
                {processing.has(m.id) ? "Connecting…" : "Connect"}
              </button>
            </article>
          ))}
        </div>
      ) : (
        <CompactEmpty
          icon={Users}
          title="No members found"
          text="Try a broader search or check again later."
        />
      )}
    </div>
  );
}

function NetworkSection({
  title,
  description,
  rows,
  empty,
  actions,
  connected,
}) {
  const headingId = `network-${title.replace(/\s/g, "-").toLowerCase()}`;
  return (
    <section className="mt-8" aria-labelledby={headingId}>
      <h3 id={headingId} className="font-display text-lg text-dir-text">
        {title}
      </h3>
      <p className="mt-1 text-xs text-dir-faint">{description}</p>
      {!rows.length ? (
        <p className="mt-3 rounded-xl border border-dir-gold/10 bg-dir-card/40 px-4 py-3 text-sm text-dir-muted">
          {empty}
        </p>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {rows.map((connection) => (
            <article
              key={connection.connection_id}
              className={`${panel} min-w-0 p-4`}
            >
              <div className="flex min-w-0 items-center gap-3">
                <Avatar user={connection} />
                <div className="min-w-0 flex-1">
                  <h4 className="truncate font-semibold text-dir-text">
                    {fullName(connection)}
                  </h4>
                  <p className="truncate text-xs text-dir-muted">
                    {[connection.country, connection.city]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  {[
                    connection.job_title || connection.profession,
                    connection.organisation,
                  ].filter(Boolean).length > 0 && (
                    <p className="mt-1 truncate text-xs text-dir-faint">
                      {[
                        connection.job_title || connection.profession,
                        connection.organisation,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-4 flex min-h-11 flex-wrap items-center gap-2">
                {connected && (
                  <span className="inline-flex min-h-10 items-center gap-2 text-sm font-semibold text-emerald-300">
                    <Check className="h-4 w-4" />
                    Connected
                  </span>
                )}
                <div className="ml-auto flex flex-wrap justify-end gap-2">
                  {actions(connection)}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function ConnectionAction({ children, primary, ...props }) {
  return (
    <button
      {...props}
      className={`min-h-10 rounded-full px-4 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${primary ? "bg-dir-gold text-dir-deep hover:brightness-110" : "border border-dir-gold/30 text-dir-gold hover:bg-dir-gold/10"}`}
    >
      {children}
    </button>
  );
}

function NotificationsTab({ rows, onChanged }) {
  const markAll = () =>
    api
      .put("/me/notifications/read-all", {})
      .then(() => {
        toast.success("All notifications marked as read.");
        onChanged();
      })
      .catch(() => toast.error("Could not update notifications."));
  const read = (n) =>
    !n.read_at && api.put(`/me/notifications/${n.id}`, {}).then(onChanged);
  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <SectionTitle
          title="Notifications"
          text="Connection, content and account updates."
        />
        {rows.some((x) => !x.read_at) && (
          <button
            onClick={markAll}
            className="min-h-11 text-sm font-semibold text-dir-gold"
          >
            Mark all read
          </button>
        )}
      </div>
      {!rows.length ? (
        <CompactEmpty
          icon={Bell}
          title="No notifications"
          text="You're all caught up."
        />
      ) : (
        <div className="mt-6 space-y-3">
          {rows.map((n) => (
            <Link
              onClick={() => read(n)}
              to={n.url || "/dashboard"}
              key={n.id}
              className={`${panel} flex items-start gap-4 p-4 ${!n.read_at ? "border-dir-gold/35 bg-dir-gold/5" : ""}`}
            >
              <span
                className={`mt-2 h-2.5 w-2.5 shrink-0 rounded-full ${n.read_at ? "bg-dir-muted/30" : "bg-dir-gold"}`}
              />
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-dir-text">
                  {n.title}
                </h3>
                <p className="mt-1 text-sm text-dir-muted">{n.body}</p>
                <time className="mt-2 block text-xs text-dir-faint">
                  {new Date(n.created_at).toLocaleString()}
                </time>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function AccountSettings({ user, setUser, logout }) {
  const [password, setPassword] = useState(""),
    [confirm, setConfirm] = useState(""),
    [saving, setSaving] = useState(false),
    [prefs, setPrefs] = useState(
      user.notification_preferences || {
        connections: true,
        comments: true,
        announcements: true,
      },
    );
  const savePrefs = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/profile", {
        ...user,
        notification_preferences: prefs,
      });
      setUser({ ...user, ...data });
      toast.success("Preferences saved.");
    } catch {
      toast.error("Could not save preferences.");
    } finally {
      setSaving(false);
    }
  };
  const changePassword = async (e) => {
    e.preventDefault();
    if (password.length < 8 || password !== confirm) {
      toast.error("Passwords must match and contain at least 8 characters.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/me/account", { password });
      setPassword("");
      setConfirm("");
      toast.success("Password updated successfully.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mx-auto max-w-3xl space-y-7">
      <SectionTitle
        title="Account Settings"
        text="Security, sessions and communication preferences—not profile details."
      />
      <FormSection title="Account">
        <Label text="Email">
          <input
            value={user.email || ""}
            readOnly
            className={`${control} opacity-75`}
          />
        </Label>
      </FormSection>
      <form onSubmit={changePassword}>
        <FormSection title="Change Password">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="New Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Input
              label="Confirm New Password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          <GoldButton className="mt-4" disabled={saving}>
            <LockKeyhole className="h-4 w-4" />
            Update Password
          </GoldButton>
        </FormSection>
      </form>
      <FormSection title="Notification Preferences">
        {[
          ["connections", "Connection requests"],
          ["comments", "Comments and replies"],
          ["announcements", "Admin announcements"],
        ].map(([key, label]) => (
          <label
            key={key}
            className="flex min-h-12 items-center justify-between gap-4 border-b border-dir-gold/10 py-3 text-sm text-dir-text last:border-0"
          >
            <span>{label}</span>
            <input
              type="checkbox"
              checked={prefs[key] !== false}
              onChange={(e) => setPrefs({ ...prefs, [key]: e.target.checked })}
              className="h-5 w-5 accent-dir-gold"
            />
          </label>
        ))}
        <GoldButton className="mt-4" onClick={savePrefs} disabled={saving}>
          Save Preferences
        </GoldButton>
      </FormSection>
      <FormSection title="Session">
        <button
          onClick={logout}
          className="inline-flex min-h-11 items-center gap-2 rounded-full border border-dir-gold/35 px-5 text-sm font-semibold text-dir-gold"
        >
          <LogOut className="h-4 w-4" />
          Log Out
        </button>
      </FormSection>
    </div>
  );
}

function FormSection({ title, text, children }) {
  return (
    <section className={`${panel} p-5 sm:p-6`}>
      <h2 className="font-display text-lg font-semibold text-dir-text">
        {title}
      </h2>
      {text && <p className="mt-1 text-xs leading-5 text-dir-muted">{text}</p>}
      <div className="mt-5 space-y-4">{children}</div>
    </section>
  );
}
function SectionTitle({ title, text }) {
  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-dir-text">{title}</h1>
      <p className="mt-1 text-sm text-dir-muted">{text}</p>
    </div>
  );
}
function Label({ text, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm text-dir-muted">{text}</span>
      {children}
    </label>
  );
}
function CompactEmpty({ icon: Icon, title, text, action, to }) {
  const body = (
    <>
      <Icon className="h-7 w-7 text-dir-gold/60" />
      <div>
        <h3 className="text-sm font-semibold text-dir-text">{title}</h3>
        <p className="mt-1 text-xs leading-5 text-dir-muted">{text}</p>
        {action && (
          <span className="mt-2 inline-flex text-xs font-semibold text-dir-gold">
            {action} →
          </span>
        )}
      </div>
    </>
  );
  return to ? (
    <Link
      to={to}
      className="mt-6 flex items-start gap-3 rounded-xl border border-dir-gold/15 bg-dir-deep p-5"
    >
      {body}
    </Link>
  ) : (
    <div className="mt-6 flex items-start gap-3 rounded-xl border border-dir-gold/15 bg-dir-deep p-5">
      {body}
    </div>
  );
}
function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="skeleton h-16 rounded-2xl" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SkeletonCards count={4} />
      </div>
      <div className="skeleton h-64 rounded-2xl" />
    </div>
  );
}
function SkeletonCards({ count }) {
  return Array.from({ length: count }, (_, i) => (
    <div key={i} className="skeleton h-36 rounded-2xl" />
  ));
}
